import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  MessageSquare, ThumbsUp, ThumbsDown, Minus, Hash, Loader2, Search,
  Languages, Heart, ArrowUpDown, Star, History, Trash2, X, ExternalLink,
  Youtube,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API, formatNumber, SENTIMENT_COLORS, LANGUAGE_COLORS, getPlatformBadgeClass } from "./constants";

const SENTIMENT_ICONS = { positive: ThumbsUp, negative: ThumbsDown, neutral: Minus };

const UCA_T = {
  ko: {
    enterUrl: "URL 또는 ID를 입력하세요",
    deleted: "삭제되었습니다",
    allDeleted: "전체 삭제되었습니다",
    top: "인기순",
    newest: "최신순",
    analyzing: "분석 중...",
    analyze: "댓글 분석",
    history: "분석 기록",
    comments: "수집된 댓글",
    sentiment: "전체 감성",
    positive: "긍정",
    negative: "부정",
    neutral: "중립",
    languages: "언어 수",
    keywords: "주요 키워드",
    sentimentChart: "감성 분석",
    commentLabel: "댓글",
    langDist: "언어 분포",
    noData: "데이터 없음",
    sortLikes: "좋아요순",
    sortNewest: "최신순",
    noComments: "댓글이 없습니다",
    analyzedAt: "분석 시간",
    emptyTitle: "멀티 플랫폼 댓글 감성 분석",
    emptyDesc: "플랫폼을 선택하고 게시물 URL 또는 ID를 입력하면 댓글을 수집하고 AI를 사용하여 감성, 언어, 키워드를 분석합니다.",
    deleteAll: "전체 삭제",
    noHistory: "분석 기록이 없습니다",
    view: "보기",
    aiSummary: "AI 분석 요약",
    multiBatchNote: "300개 이상은 여러 배치로 분석되어 시간이 더 걸릴 수 있습니다",
  },
  en: {
    enterUrl: "Enter a URL or ID",
    deleted: "Deleted",
    allDeleted: "All deleted",
    top: "Top",
    newest: "Newest",
    analyzing: "Analyzing...",
    analyze: "Analyze",
    history: "History",
    comments: "Comments",
    sentiment: "Sentiment",
    positive: "Positive",
    negative: "Negative",
    neutral: "Neutral",
    languages: "Languages",
    keywords: "Keywords",
    sentimentChart: "Sentiment",
    commentLabel: "comments",
    langDist: "Languages",
    noData: "No data",
    sortLikes: "Likes",
    sortNewest: "Newest",
    noComments: "No comments",
    analyzedAt: "Analyzed",
    emptyTitle: "Multi-Platform Comment Analysis",
    emptyDesc: "Select a platform and enter a post URL or ID to collect comments and analyze sentiment, language, and keywords using AI.",
    deleteAll: "Delete All",
    noHistory: "No history",
    view: "View",
    aiSummary: "AI Summary",
    multiBatchNote: "Over 300 comments are analyzed in multiple batches and may take longer",
  },
  zh: {
    enterUrl: "请输入 URL 或 ID",
    deleted: "已删除",
    allDeleted: "全部已删除",
    top: "热门",
    newest: "最新",
    analyzing: "分析中...",
    analyze: "分析",
    history: "历史记录",
    comments: "收集的评论",
    sentiment: "整体情感",
    positive: "正面",
    negative: "负面",
    neutral: "中立",
    languages: "语言数",
    keywords: "关键词",
    sentimentChart: "情感分析",
    commentLabel: "评论",
    langDist: "语言分布",
    noData: "无数据",
    sortLikes: "按点赞排序",
    sortNewest: "按时间排序",
    noComments: "暂无评论",
    analyzedAt: "分析时间",
    emptyTitle: "多平台评论情感分析",
    emptyDesc: "选择平台并输入帖子 URL 或 ID，即可收集评论并使用 AI 分析情感、语言和关键词。",
    deleteAll: "全部删除",
    noHistory: "暂无历史记录",
    view: "查看",
    aiSummary: "AI 分析摘要",
    multiBatchNote: "超过300条评论将分批分析，可能需要更长时间",
  },
};

const PLATFORM_CONFIG = [
  { id: "youtube", label: "YouTube", color: "#FF0000", icon: Youtube, placeholder: "YouTube URL (https://youtube.com/watch?v=...) or Video ID", extract: (input) => { const t = input.trim(); if (/^[a-zA-Z0-9_-]{11}$/.test(t)) return t; const m = t.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/); return m ? m[1] : t; } },
  { id: "tiktok", label: "TikTok", color: "#25282B", placeholder: "TikTok Video ID (aweme_id) or URL", extract: (input) => { const t = input.trim(); const m = t.match(/video\/(\d+)/); return m ? m[1] : t.replace(/\D/g, "") || t; } },
  { id: "douyin", label: "Douyin", color: "#25F4EE", placeholder: "Douyin Video ID or Share URL", extract: (input) => { const t = input.trim(); const m = t.match(/video\/(\d+)/); return m ? m[1] : t.replace(/\D/g, "") || t; } },
  { id: "instagram", label: "Instagram", color: "#833AB4", placeholder: "Instagram Post Shortcode or URL", extract: (input) => { const t = input.trim(); const m = t.match(/\/p\/([A-Za-z0-9_-]+)/); return m ? m[1] : t; } },
  { id: "weibo", label: "Weibo", color: "#FF8C00", placeholder: "Weibo Post ID or URL", extract: (input) => { const t = input.trim(); const m = t.match(/\/(\d+)/); return m ? m[1] : t; } },
  { id: "xiaohongshu", label: "XHS", color: "#FACC15", placeholder: "Xiaohongshu Note ID or URL", extract: (input) => { const t = input.trim(); const m = t.match(/explore\/([a-f0-9]+)|discovery\/item\/([a-f0-9]+)/); return m ? (m[1] || m[2]) : t; } },
  { id: "bilibili", label: "Bilibili", color: "#F47295", placeholder: "Bilibili BV Number or URL", extract: (input) => { const t = input.trim(); const m = t.match(/(BV[a-zA-Z0-9]+)/); return m ? m[1] : t; } },
];

const SentimentLabel = ({ sentiment }) => {
  const labels = { positive: "Positive", negative: "Negative", neutral: "Neutral" };
  const labelsKo = { positive: "Positive", negative: "Negative", neutral: "Neutral" };
  return labels[sentiment] || sentiment;
};

const UnifiedCommentAnalysis = ({ lang = "ko" }) => {
  const ut = UCA_T[lang] || UCA_T.ko;
  const [searchParams, setSearchParams] = useSearchParams();
  const [platform, setPlatform] = useState(() => {
    const p = searchParams.get("platform");
    return PLATFORM_CONFIG.find(c => c.id === p) ? p : "youtube";
  });
  const [inputValue, setInputValue] = useState(() => searchParams.get("post_id") || "");
  const [maxComments, setMaxComments] = useState("100");
  const [sortBy, setSortBy] = useState("top");
  const autoTriggered = useRef(false);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentSort, setCommentSort] = useState("likes");
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const currentConfig = PLATFORM_CONFIG.find(p => p.id === platform);

  const runAnalysis = useCallback(async () => {
    const postId = currentConfig.extract(inputValue);
    if (!postId) {
      toast.error(ut.enterUrl);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/comments/unified/analyze`, null, {
        params: { platform, post_id: postId, max_comments: parseInt(maxComments, 10), sort_by: sortBy, title: inputValue.slice(0, 100), language: lang },
      });
      setAnalysis(res.data);

      const commRes = await axios.get(`${API}/comments/unified/stored/${platform}/${postId}`, {
        params: { limit: 100, sort: commentSort },
      });
      setComments(commRes.data.comments || []);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [inputValue, platform, maxComments, sortBy, commentSort, currentConfig, lang]);

  // Auto-trigger analysis when navigated from Posts page with query params
  useEffect(() => {
    if (autoTriggered.current) return;
    const paramPlatform = searchParams.get("platform");
    const paramPostId = searchParams.get("post_id");
    if (paramPlatform && paramPostId && PLATFORM_CONFIG.find(c => c.id === paramPlatform)) {
      autoTriggered.current = true;
      // Clear search params so refresh doesn't re-trigger
      setSearchParams({}, { replace: true });
      // Trigger analysis after state is set
      setTimeout(() => {
        runAnalysis();
      }, 100);
    }
  }, [searchParams, setSearchParams, runAnalysis]);

  const loadComments = async (sort) => {
    if (!analysis?.post_id) return;
    setCommentSort(sort);
    try {
      const res = await axios.get(`${API}/comments/unified/stored/${analysis.platform}/${analysis.post_id}`, {
        params: { limit: 100, sort },
      });
      setComments(res.data.comments || []);
    } catch (err) {
      console.error("Failed to reload comments:", err);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/comments/unified/history`);
      setHistoryData(res.data.history || []);
      setShowHistory(true);
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const restoreFromHistory = async (item) => {
    setAnalysis(item);
    setPlatform(item.platform);
    setInputValue(item.title || item.post_id);
    setShowHistory(false);
    try {
      const commRes = await axios.get(`${API}/comments/unified/stored/${item.platform}/${item.post_id}`, {
        params: { limit: 100, sort: "likes" },
      });
      setComments(commRes.data.comments || []);
    } catch {
      setComments([]);
    }
  };

  const deleteHistoryItem = async (p, pid) => {
    try {
      await axios.delete(`${API}/comments/unified/history/${p}/${pid}`);
      setHistoryData(prev => prev.filter(h => !(h.platform === p && h.post_id === pid)));
      toast.success(ut.deleted);
      if (analysis?.platform === p && analysis?.post_id === pid) {
        setAnalysis(null);
        setComments([]);
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  const deleteAllHistory = async () => {
    try {
      await axios.delete(`${API}/comments/unified/history-all`);
      setHistoryData([]);
      toast.success(ut.allDeleted);
    } catch {
      toast.error("Delete failed");
    }
  };

  const sentimentBreakdown = analysis?.sentiment_breakdown || [];
  const langBreakdown = analysis?.language_breakdown || [];
  const keywords = analysis?.keywords || [];

  return (
    <div className="space-y-6" data-testid="unified-comment-analysis">
      {/* Platform Selector + Search */}
      <Card className="bg-card border-border/50">
        <CardContent className="pt-5 space-y-4">
          {/* Platform tabs */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="platform-selector">
            {PLATFORM_CONFIG.map((p) => (
              <button
                key={p.id}
                onClick={() => { setPlatform(p.id); setAnalysis(null); setComments([]); setError(null); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  platform === p.id
                    ? "text-white shadow-md"
                    : "text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted"
                }`}
                style={platform === p.id ? { backgroundColor: p.color } : {}}
                data-testid={`platform-tab-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Input row */}
          <div className="flex flex-col gap-3 lg:flex-row">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="post-id-input"
                placeholder={currentConfig?.placeholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runAnalysis()}
                className="pl-10"
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:w-auto lg:grid-cols-[90px_90px_auto]">
              <Select value={maxComments} onValueChange={setMaxComments}>
                <SelectTrigger className="w-full text-xs sm:w-[90px]" data-testid="max-comments-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="300">300</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full text-xs sm:w-[90px]" data-testid="sort-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">{ut.top}</SelectItem>
                  <SelectItem value="newest">{ut.newest}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                data-testid="analyze-btn"
                onClick={runAnalysis}
                disabled={loading || !inputValue.trim()}
                style={{ backgroundColor: currentConfig?.color }}
                className="w-full justify-center text-white hover:opacity-90 lg:w-auto"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {loading ? ut.analyzing : ut.analyze}
              </Button>
            </div>
          </div>

          {/* Multi-batch notice */}
          {parseInt(maxComments, 10) > 300 && (
            <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">{ut.multiBatchNote}</p>
          )}

          {/* History button */}
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={loadHistory} disabled={loadingHistory} className="text-xs text-muted-foreground" data-testid="history-btn">
              {loadingHistory ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <History className="h-3 w-3 mr-1" />}
              {ut.history}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="bg-destructive/10 border-destructive/30">
          <CardContent className="pt-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {/* History Panel */}
      {showHistory && (
        <HistoryPanel
          historyData={historyData}
          onRestore={restoreFromHistory}
          onDelete={deleteHistoryItem}
          onDeleteAll={deleteAllHistory}
          onClose={() => setShowHistory(false)}
          lang={lang}
        />
      )}

      {/* Analysis Results */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <SummaryCard icon={MessageSquare} iconColor={currentConfig?.color || "#666"} label={ut.comments} value={formatNumber(analysis.total_comments_collected)} testId="total-comments-count" />
            <SummaryCard icon={SENTIMENT_ICONS[analysis.overall_sentiment] || Minus} iconColor="#10b981" label={ut.sentiment} value={analysis.overall_sentiment === "positive" ? ut.positive : analysis.overall_sentiment === "negative" ? ut.negative : ut.neutral} testId="overall-sentiment" />
            <SummaryCard icon={Languages} iconColor="#06b6d4" label={ut.languages} value={langBreakdown.length} testId="language-count" />
            <SummaryCard icon={Hash} iconColor="#f97316" label={ut.keywords} value={keywords.length} testId="keywords-count" />
          </div>

          {/* AI Summary */}
          {(analysis.summary || analysis.summary_ko) && (
            <Card className="bg-card border-border/50 border-l-4" style={{ borderLeftColor: currentConfig?.color }}>
              <CardContent className="pt-4">
                <p className="text-sm font-medium text-muted-foreground mb-1">{ut.aiSummary}</p>
                <p className="text-sm leading-relaxed" data-testid="analysis-summary">{analysis.summary || analysis.summary_ko}</p>
              </CardContent>
            </Card>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sentiment Pie */}
            <Card className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Heart className="h-4 w-4 text-pink-500" /> {ut.sentimentChart}</CardTitle></CardHeader>
              <CardContent>
                {sentimentBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={sentimentBreakdown.map(s => ({ name: s.sentiment, value: s.count, color: SENTIMENT_COLORS[s.sentiment] }))} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value">
                          {sentimentBreakdown.map((s, i) => (<Cell key={i} fill={SENTIMENT_COLORS[s.sentiment]} />))}
                        </Pie>
                        <Tooltip formatter={(v) => [v, ut.commentLabel]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-4 flex-wrap">
                      {sentimentBreakdown.map((s, i) => {
                        const Icon = SENTIMENT_ICONS[s.sentiment];
                        return (
                          <div key={i} className="flex items-center gap-1.5 text-xs">
                            <Icon className="h-3 w-3" style={{ color: SENTIMENT_COLORS[s.sentiment] }} />
                            <span>{s.sentiment === "positive" ? ut.positive : s.sentiment === "negative" ? ut.negative : ut.neutral}</span>
                            <span className="font-mono text-muted-foreground">({s.percentage}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground text-center py-4">{ut.noData}</p>}
              </CardContent>
            </Card>

            {/* Language Distribution */}
            <Card className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Languages className="h-4 w-4 text-cyan-500" /> {ut.langDist}</CardTitle></CardHeader>
              <CardContent>
                {langBreakdown.length > 0 ? (
                  <div className="space-y-3">
                    {langBreakdown.slice(0, 6).map((l, i) => (
                      <div key={i} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: LANGUAGE_COLORS[l.language_code] || "#64748B" }} />
                            {l.language}
                          </span>
                          <span className="font-mono">{l.percentage}%</span>
                        </div>
                        <Progress value={l.percentage} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground text-center py-4">{ut.noData}</p>}
              </CardContent>
            </Card>

            {/* Keywords */}
            <Card className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Hash className="h-4 w-4 text-orange-500" /> {ut.keywords}</CardTitle></CardHeader>
              <CardContent>
                {keywords.length > 0 ? (
                  <div className="space-y-2">
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={keywords.slice(0, 8)} layout="vertical" margin={{ left: 0, right: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="keyword" width={80} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {keywords.slice(0, 8).map((kw, i) => (<Cell key={i} fill={SENTIMENT_COLORS[kw.sentiment] || "#64748B"} />))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {keywords.map((kw, i) => (
                        <Badge key={i} variant="outline" className="text-xs" style={{ borderColor: (SENTIMENT_COLORS[kw.sentiment] || "#64748B") + "50", backgroundColor: (SENTIMENT_COLORS[kw.sentiment] || "#64748B") + "10" }}>
                          {kw.keyword} <span className="ml-1 text-muted-foreground">({kw.count})</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground text-center py-4">{ut.noData}</p>}
              </CardContent>
            </Card>
          </div>

          {/* Comments List */}
          <Card className="bg-card border-border/50">
            <CardHeader className="space-y-2 sm:space-y-0 sm:flex-row flex-col sm:flex sm:items-center sm:justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <MessageSquare className="h-4 w-4" /> {ut.comments} ({comments.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button data-testid="sort-comments-likes" variant={commentSort === "likes" ? "default" : "outline"} size="sm" className="text-xs" onClick={() => loadComments("likes")}>
                  <Star className="h-3 w-3 mr-1" /> {ut.sortLikes}
                </Button>
                <Button data-testid="sort-comments-newest" variant={commentSort === "newest" ? "default" : "outline"} size="sm" className="text-xs" onClick={() => loadComments("newest")}>
                  <ArrowUpDown className="h-3 w-3 mr-1" /> {ut.sortNewest}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {comments.map((c, i) => (
                    <div key={c.comment_id || i} className="p-3 rounded-lg border border-border/30 hover:border-border/60 transition-colors" data-testid={`comment-item-${i}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{c.author}</span>
                          {c.is_creator && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Creator</Badge>}
                          {c.is_verified && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Verified</Badge>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                          <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> {c.likes || 0}</span>
                          {c.reply_count > 0 && <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {c.reply_count}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed">{c.text}</p>
                      {c.published_at && <p className="text-xs text-muted-foreground mt-1">{c.published_at}</p>}
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">{ut.noComments}</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Meta Info */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`${getPlatformBadgeClass(analysis.platform)} text-xs capitalize`}>{analysis.platform}</Badge>
              <span>Source: {analysis.source}</span>
            </div>
            <span>{ut.analyzedAt}: {analysis.analyzed_at ? new Date(analysis.analyzed_at).toLocaleString(lang === "ko" ? "ko-KR" : lang === "zh" ? "zh-CN" : "en-US") : "-"}</span>
          </div>
        </>
      )}

      {/* Empty State */}
      {!analysis && !loading && !showHistory && (
        <Card className="bg-card border-border/50">
          <CardContent className="py-16 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{ut.emptyTitle}</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {ut.emptyDesc}
            </p>
            <div className="flex justify-center gap-2 mt-6 flex-wrap">
              {PLATFORM_CONFIG.map(p => (
                <Badge key={p.id} variant="outline" className="text-xs" style={{ borderColor: p.color + "50", color: p.color }}>{p.label}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Summary Card sub-component
const SummaryCard = ({ icon: Icon, iconColor, label, value, testId }) => (
  <Card className="bg-card border-border/50">
    <CardContent className="pt-4 px-3 sm:px-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: iconColor + "15" }}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: iconColor }} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{label}</p>
          <p className="text-xl sm:text-2xl font-bold font-mono" data-testid={testId}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// History Panel sub-component
const HistoryPanel = ({ historyData, onRestore, onDelete, onDeleteAll, onClose, lang }) => {
  const ut = UCA_T[lang] || UCA_T.ko;
  return (
  <Card className="bg-card border-border/50" data-testid="analysis-history-panel">
    <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
      <CardTitle className="text-base font-heading flex items-center gap-2">
        <History className="h-4 w-4 text-muted-foreground" />
        {ut.history} ({historyData.length})
      </CardTitle>
      <div className="flex flex-wrap items-center gap-2">
        {historyData.length > 0 && (
          <Button variant="outline" size="sm" onClick={onDeleteAll} className="text-xs text-destructive hover:text-destructive border-destructive/30" data-testid="delete-all-btn">
            <Trash2 className="h-3 w-3 mr-1" /> {ut.deleteAll}
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {historyData.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">{ut.noHistory}</p>
      ) : (
        <div className="h-[400px] overflow-y-auto space-y-2 pr-1">
          {historyData.map((item, idx) => {
            const topSent = item.sentiment_breakdown?.[0];
            const SentIcon = topSent ? SENTIMENT_ICONS[topSent.sentiment] : Minus;
            return (
              <div key={idx} className="p-3 rounded-lg border border-border/30 hover:border-border/60 transition-colors" data-testid={`history-item-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{item.title || item.post_id}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className={`${getPlatformBadgeClass(item.platform)} text-xs capitalize`}>{item.platform}</Badge>
                      <span className="text-xs text-muted-foreground">{item.total_comments_collected || 0} {ut.commentLabel}</span>
                      {topSent && (
                        <span className="text-xs flex items-center gap-1">
                          <SentIcon className="h-3 w-3" style={{ color: SENTIMENT_COLORS[topSent.sentiment] }} />
                          {topSent.percentage}%
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground/60">
                        {item.analyzed_at ? new Date(item.analyzed_at).toLocaleDateString(lang === "ko" ? "ko-KR" : lang === "zh" ? "zh-CN" : "en-US") : ""}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => onRestore(item)} className="text-xs text-cyan-400 h-7 px-2" data-testid={`restore-history-${idx}`}>
                      {ut.view}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(item.platform, item.post_id)} data-testid={`delete-history-${idx}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default UnifiedCommentAnalysis;
